import { ChevronDown } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem
} from '@/components/ui/DropdownMenu';
import type { DetailZustand } from '@/types/detail';
import { zustandAnzeige, zustaendeFuerBestellung } from '@/lib/zustandLabel';
import { useZustaende } from '@/hooks/useZustaende';
import { FARBE_ZUSTAND_FALLBACK } from '@/theme';
import { zustandTextfarbe } from '@/lib/zustandFarbe';

type Props = {
  current: DetailZustand;
  /**
   * Code der Versandart dieser Bestellung.
   *
   * Zwei Dinge haengen daran: Der Zustand `bereit` heisst bei einer
   * Abholung „Abholbereit" — und er wird bei Versand gar nicht mehr
   * angeboten (A46). Der Zustandscode selbst bleibt `bereit`; im Kanban
   * und in den Auswertungen aendert sich nichts.
   */
  versandartCode: string | null;
  onSelect: (zustandCode: string) => void;
};

export function ZustandSwitcher({ current, versandartCode, onSelect }: Props) {
  const q = useZustaende();
  // E61: als Schrift abgedunkelt, falls die gepflegte Farbe zu hell ist.
  const farbe = zustandTextfarbe(current.color);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="inline-flex min-h-[44px] items-center gap-2 rounded-full border px-3 py-1 text-sm hover:bg-cream-warm"
          style={{ borderColor: farbe, color: farbe }}
        >
          <span aria-hidden className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: farbe }} />
          {zustandAnzeige(current.code, current.label, versandartCode)}
          <ChevronDown className="h-4 w-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        {/* Nur, was für DIESE Bestellung möglich ist (A46): Bei Versand
            fehlt „Bereit" — dorthin führt kein Weg mehr, und der Server
            weist ihn ab. */}
        {zustaendeFuerBestellung(q.data?.items, versandartCode).map((z) => (
          <DropdownMenuItem
            key={z.id}
            onSelect={() => {
              if (z.code !== current.code) onSelect(z.code);
            }}
          >
            <span
              aria-hidden
              className="h-2 w-2 rounded-full"
              style={{ backgroundColor: z.farbe ?? FARBE_ZUSTAND_FALLBACK }}
            />
            {zustandAnzeige(z.code, z.label, versandartCode)}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
